import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { CustomDialog } from '../CustomDialog';
import { KebabMenu } from '../common/KebabMenu';
import { fetchExternalVideoThumbnail } from '../../services/externalVideoApi/externalVideoApi';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { getThumbnailUrl, getDefaultThumbnailUrl, isThumbnailPending } from '../../utils/thumbnailUtils';
import { normalizeVideoImagePath, toRelativePath } from '../../utils/videoImagePath';
import { VISIBILITY_BADGE_LABELS } from '../../utils/visibilityLabels';
import { FALLBACK_LABELS } from '../../config/constants';

import { useOriginAccess } from '../../hooks/useOriginAccess';
import { hasSpecialtyAssigned } from '../../utils/videoSpecialtyStatus';
import { formatDate, formatDuration, getElapsedTime } from './utils';
import { useVideoCard } from './useVideoCard';
import { VideoCardProps, ThumbnailImageProps } from './types';
import styles from './VideoCard.module.css';

// サムネイル画像コンポーネント（画像全体がコンテナ内に収まり、中央に表示される）
const ThumbnailImage = ({ src, alt, defaultStyle }: ThumbnailImageProps) => {
  const [hasError, setHasError] = useState<boolean>(false);
  
  const handleError = () => {
    setHasError(true);
  };
  
  return (
    <img
      src={hasError ? getDefaultThumbnailUrl() : src}
      alt={alt}
      style={{
        ...defaultStyle,
        width: '100%',
        height: '100%',
        objectFit: (defaultStyle && defaultStyle.objectFit) || 'contain',
        objectPosition: 'center',
        borderRadius: '8px',
      }}
      onError={handleError}
    />
  );
};

/**
 * 外部動画カードコンポーネント
 */
const ExternalVideoCard = ({ video }: VideoCardProps) => {
  const navigate = useNavigate();

  const [thumbnailBlobUrl, setThumbnailBlobUrl] = useState<string | undefined>(undefined);
  const blobUrlRef = useRef<string>(undefined);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isMenuOpenRef = useRef(false);

  const toggleMenu = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(prev => {
      if (prev) return false;
      if (!buttonRef.current) return false;
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const menuWidth = viewportWidth <= 768 ? 120 : 140;
      let leftPosition = rect.left;
      if (leftPosition + menuWidth > viewportWidth) {
        leftPosition = viewportWidth - menuWidth - 10;
      }
      if (leftPosition < 10) leftPosition = 10;
      setMenuPosition({ top: rect.bottom, left: leftPosition });
      return true;
    });
  }, []);

  const closeMenu = useCallback(() => setIsMenuOpen(false), []);

  useEffect(() => {
    isMenuOpenRef.current = isMenuOpen;
  }, [isMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isMenuOpenRef.current) return;
      const target = event.target as Node;
      if (
        (menuRef.current && menuRef.current.contains(target)) ||
        (buttonRef.current && buttonRef.current.contains(target))
      ) return;
      setIsMenuOpen(false);
    };
    const handleScroll = () => {
      if (isMenuOpenRef.current) setIsMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  useEffect(() => {
    if (!video.external_id) return;
    let revoked = false;

    fetchExternalVideoThumbnail(video.external_id)
      .then(blobUrl => {
        if (!revoked) {
          blobUrlRef.current = blobUrl;
          setThumbnailBlobUrl(blobUrl);
        } else {
          URL.revokeObjectURL(blobUrl);
        }
      })
      .catch((err: unknown) => {
        // サムネイル取得失敗時はプレースホルダーを表示（thumbnailBlobUrl が undefined のまま）
        console.warn('外部動画サムネイル取得失敗:', video.external_id, err);
      });

    return () => {
      revoked = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = undefined;
      }
    };
  }, [video.external_id]);

  const handleClick = () => {
    if (video.external_id && video.external_status === 'synced') {
      navigate(`/external-videos/${video.external_id}`);
    }
  };

  const isPlayable = video.external_status === 'synced';

  return (
    <div
      className={`${styles['video-card-container']} ${isPlayable ? styles['video-card-container--playable'] : styles['video-card-container--disabled']}`}
      data-testid="video-card-external"
      onClick={handleClick}
    >
      {/* サムネイル */}
      <div className={styles['video-card-thumbnail-wrapper']}>
        {thumbnailBlobUrl ? (
          <ThumbnailImage src={thumbnailBlobUrl} alt={video.title || video.name || FALLBACK_LABELS.UNTITLED} defaultStyle={{ objectFit: 'cover' }} />
        ) : (
          <div className={styles['external-thumbnail-placeholder']}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" stroke="#999" strokeWidth="1.5"/>
              <polygon points="9.5,8 16.5,12 9.5,16" fill="#999"/>
            </svg>
          </div>
        )}
        {/* 再生時間 */}
        <div className={styles['video-duration']}>
          {formatDuration(video.duration)}
        </div>
      </div>

      <div className={styles['video-card-info-wrapper']}>
        <div className={styles['video-card-info']}>
          <h3 className={styles['video-card-title-wrapper']}>
            <span className={styles['video-card-title']} title={video.title || video.name || FALLBACK_LABELS.UNTITLED}>
              {video.title || video.name || FALLBACK_LABELS.UNTITLED}
            </span>
            <KebabMenu
              isOpen={isMenuOpen}
              position={menuPosition}
              onClose={closeMenu}
              isFavorite={false}
              isOwner={false}
              isPrivate={false}
              currentVisibility="public"
              hideVisibilityToggle={true}
              hideFavorite={true}
              hideEdit={true}
              onFavoriteClick={() => {}}
              onVisibilityClick={() => {}}
              onEditClick={() => {}}
              onDeleteClick={() => {}}
              menuRef={menuRef}
              buttonRef={buttonRef}
              onToggle={toggleMenu}
              testIdPrefix="video-card-external"
            />
          </h3>
          <div className={styles['video-card-meta-wrapper']}>
            <img
              src={video.uploader?.icon_path ? toRelativePath(video.uploader.icon_path) : '/asset/no_icon_user.png'}
              alt="Uploader Icon"
              className={styles['video-card-avatar']}
            />
            <div className={styles['video-card-meta-text']}>
              <p className={styles['video-card-channel-name']} title={video.hospital_name || FALLBACK_LABELS.EXTERNAL_HOSPITAL}>
                {video.hospital_name || FALLBACK_LABELS.EXTERNAL_HOSPITAL}
              </p>
              <p className={styles['video-card-details']}>
                {`投稿日:${formatDate(video.created_at)}`}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MemoizedExternalVideoCard = memo(ExternalVideoCard);

/**
 * 動画カードコンポーネント（振り分け）
 */
const VideoCard = (props: VideoCardProps) => {
  if (props.video.isExternal) {
    return <MemoizedExternalVideoCard {...props} />;
  }
  return <InternalVideoCard {...props} />;
};

/**
 * 自院動画カードコンポーネント
 * 動画情報と付随する操作を表示するカード形式のUI
 */
const InternalVideoCard = (props: VideoCardProps) => {
  // 接続元判定: サーバー API /api/access/origin を起動時1回取得。
  // isLoading 中は isExternal=false（活性のままサーバー 403 に委ねる / UX 原則）。
  // isExternal=true の場合、ケバブメニューのDLボタンを disabled にする（UX R1-a）。
  const { isExternal } = useOriginAccess();
  // 診療科未設定の動画は公開系メニュー（院内公開にする／外部公開にする）を disabled にする。
  // 判定は3種のカードで共通のユーティリティに集約している（videoSpecialtyStatus.ts）。
  const hasSpecialty = hasSpecialtyAssigned(props.video);

  const {
    isFavorite,
    visibility,
    dialogOpen,
    dialogConfig,
    uploaderIconUrl,
    channelLabel,
    isOwner,
    images,
    isPrivate,
    toggleFavorite,
    showVisibilityChangeDialog,
    handleContainerClick,
    handleSwiperInit,
    handleMouseEnter,
    handleMouseLeave,
    setDialogOpen,
    // ケバブメニュー関連
    kebabMenu
  } = useVideoCard(props);

  const { video, hideVisibilityToggle = false, showEditButton = false } = props;

  // 処理中動画の判定: is_processingフラグまたはIDがsession_で始まる場合
  const isVideoProcessing = video.is_processing || String(video.id).startsWith('session_');

  return (
    <div
      className={styles['video-card-container']}
      data-testid="video-card"
      onClick={handleContainerClick}
    >
      <div
        className={styles['video-card-thumbnail-wrapper']}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {images && images.length > 0 ? (
          <Swiper
            modules={[Navigation, Pagination]}
            slidesPerView={1}
            loop={images && images.length > 1}
            navigation={{
              enabled: images && images.length > 1
            }}
            pagination={{ clickable: true }}
            className={styles.swiper}
            onSwiper={handleSwiperInit}
          >
            {images.map((img, idx) => (
              <SwiperSlide key={idx}>
                <ThumbnailImage
                  src={normalizeVideoImagePath(img.image_path)}
                  alt={img.description || '動画画像'}
                  defaultStyle={{ objectFit: 'contain' }}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          video.thumbnail ? (
            <ThumbnailImage
              src={getThumbnailUrl(video.thumbnail) || getDefaultThumbnailUrl()}
              alt={video.name || ''}
              defaultStyle={{ objectFit: 'cover' }}
            />
          ) : isThumbnailPending(video) ? (
            <div className={styles['spinner-container']}>
              <div className={styles.spinner}></div>
            </div>
          ) : (
            <ThumbnailImage
              src={getDefaultThumbnailUrl()}
              alt={video.name || ''}
              defaultStyle={{ objectFit: 'cover' }}
            />
          )
        )}

        {/* 再生時間 */}
        <div className={styles['video-duration']}>
          {formatDuration(video.duration)}
        </div>

        {/* 公開状態バッジ（サムネイル上オーバーレイ） */}
        {video.is_processing ? (
          <span
            className={styles['visibility-badge-overlay']}
            data-visibility="processing"
          >
            変換中
          </span>
        ) : (visibility === 'hospital_only' || visibility === 'public') && (
          <span
            className={styles['visibility-badge-overlay']}
            data-visibility={visibility}
          >
            {VISIBILITY_BADGE_LABELS[visibility]}
          </span>
        )}

        {/* 資料バッジ */}
        {!!video.materials?.length && (
          <span className={styles['materials-badge-overlay']} data-testid="video-card__materials-badge">
            資料 {video.materials.length}
          </span>
        )}

      </div>

      <div className={styles['video-card-info-wrapper']}>
        <div className={styles['video-card-info']}>
          <h3 className={styles['video-card-title-wrapper']}>
            <span className={styles['video-card-title']} title={video.title || video.name || ''}>
              {video.title || video.name || ''}
            </span>
            {/* ケバブメニュー */}
            <KebabMenu
              isOpen={kebabMenu.isMenuOpen}
              position={kebabMenu.menuPosition}
              onClose={kebabMenu.closeMenu}
              isFavorite={isFavorite}
              isOwner={isOwner}
              isPrivate={isPrivate}
              currentVisibility={visibility}
              isProcessing={isVideoProcessing}
              hideVisibilityToggle={hideVisibilityToggle || isVideoProcessing}
              hideFavorite={isVideoProcessing}
              hideEdit={false}
              isExternal={isExternal}
              hasSpecialty={hasSpecialty}
              onFavoriteClick={kebabMenu.handleFavoriteMenuClick}
              onVisibilityClick={kebabMenu.handleVisibilityMenuClick}
              onEditClick={kebabMenu.handleEditMenuClick}
              onDownloadClick={isVideoProcessing ? undefined : kebabMenu.handleDownloadMenuClick}
              onDeleteClick={kebabMenu.handleDeleteMenuClick}
              menuRef={kebabMenu.menuRef}
              buttonRef={kebabMenu.buttonRef}
              onToggle={kebabMenu.toggleMenu}
              testIdPrefix="video-card"
            />
          </h3>
          <div className={styles['video-card-meta-wrapper']}>
            <img
              src={uploaderIconUrl}
              alt="Uploader Icon"
              className={styles['video-card-avatar']}
            />
            <div className={styles['video-card-meta-text']}>
              <p className={styles['video-card-channel-name']} title={video.is_anonymous ? '匿名投稿' : (video.uploader?.hospital?.name ?? '')}>
                {video.is_anonymous ? '匿名投稿' : (video.uploader?.hospital?.name ?? '')}
              </p>
              <div className={styles['video-card-details-row']}>
                <span className={styles['video-card-details']}>{`投稿日:${formatDate(video.created_at)}`}</span>
                {video.surgery_date && (
                  <>
                    <span className={styles['video-card-details-divider']} />
                    <span className={styles['video-card-details']}>{`手術日:${formatDate(video.surgery_date)}`}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* カスタムダイアログ */}
      <CustomDialog
        isOpen={dialogOpen}
        title={dialogConfig.title}
        message={dialogConfig.message}
        confirmText={dialogConfig.confirmText}
        cancelText="キャンセル"
        onConfirm={() => {
          setDialogOpen(false);
          dialogConfig.onConfirm();
        }}
        onCancel={() => setDialogOpen(false)}
        type={dialogConfig.type}
      />

    </div>
  );
};

export default memo(VideoCard);
